/**
 * The eight steps, in the desktop application's order and wording.
 *
 * Each is a native <details>. That is not laziness: it gives keyboard operation, the
 * open/closed state in the accessibility tree, and find-in-page through collapsed content,
 * none of which a div-and-onClick accordion gets without work that is usually skipped.
 */

import { useId, useState } from "react";
import type { OutputType } from "@plan-forge/job-contract";
import {
  addressGaps,
  expandDistrictAbbreviations,
  expandSchoolTypeAbbreviations,
  portalIdIsInvalid,
} from "@plan-forge/conversion-core";
import type { PlanPage, PlanSource } from "./plan-source.ts";
import { canChooseSaveLocation } from "./save-file.ts";
import type { RecognitionResult } from "./api.ts";
import { sheetPreviewFor, sheetSummary } from "./sheet-preview.ts";
import type { ProjectSummary } from "./project-snapshot.ts";
import {
  MAX_FLOORS_PER_PAGE,
  MIN_POLYGON_POINTS,
  STEPS,
  areaDraft,
  areaKey,
  areaStatus,
  areasInPlay,
  calibrationSummary,
  completedCount,
  effectiveScale,
  floorIndexes,
  pageSetup,
  reviewLines,
  stepStatus,
  type AreaStatus,
  type CanvasMode,
  type ConversionState,
  type PageSetup,
  type SchoolDetails,
  type StepId,
} from "./state.ts";

export interface SidebarProps {
  /** Null when the build has no Entra configuration, which means the API is open. */
  readonly user: { readonly name: string | null; readonly username: string | null } | null;
  readonly onSignOut: () => void;
  readonly state: ConversionState;
  readonly source: PlanSource | null;
  readonly page: PlanPage | null;
  readonly mode: CanvasMode;
  readonly zoom: number;
  readonly results: Readonly<Record<string, RecognitionResult>>;
  readonly showResult: boolean;
  readonly blocked: string | null;
  /** Why packaging cannot run, or null. Computed by App so the reason has the result to read. */
  readonly packageBlocked: string | null;
  /** The engine's caution about this page, or null. Does not block anything. */
  readonly warning: string | null;
  readonly problem: readonly string[] | null;
  readonly uploadFraction: number | null;
  /** "Submitted 2 of 6 areas", while a run is being set off. */
  readonly progress: string | null;
  readonly projects: readonly ProjectSummary[];
  readonly projectId: string;
  readonly onPatch: (changes: Partial<ConversionState>) => void;
  readonly onDetails: (details: SchoolDetails) => void;
  readonly onFile: (file: File) => void;
  readonly onMode: (mode: CanvasMode) => void;
  readonly onZoom: (zoom: number) => void;
  readonly onGoToPage: (pageNumber: number) => void;
  readonly onPage: (pageNumber: number, change: (setup: PageSetup) => PageSetup) => void;
  readonly onFloorCount: (pageNumber: number, count: number) => void;
  readonly onSelectArea: (areaIndex: number) => void;
  readonly onDrawArea: (areaIndex: number) => void;
  readonly onClearArea: (areaIndex: number) => void;
  readonly onFinishArea: () => void;
  readonly onUndoPoint: () => void;
  readonly onToggleResult: () => void;
  readonly onRecognise: () => void;
  readonly onCreatePackage: () => void;
  readonly onFit: () => void;
  readonly onNewProject: () => void;
  readonly onOpenProject: (id: string) => void;
  readonly onDeleteProject: (id: string) => void;
}

export function Sidebar(props: SidebarProps) {
  const { state, source, page, mode, zoom, results, showResult, blocked, problem, uploadFraction } = props;
  const summary = source === null || page === null
    ? null
    : { fileName: source.fileName, pageCount: source.pageCount, preview: page.preview };
  const done = completedCount(state, summary);

  const status = (id: StepId) => stepStatus(id, state, summary);

  const thisPage = pageSetup(state, state.pageNumber);
  const activeArea = areaDraft(state, state.pageNumber, state.areaIndex);
  const inPlay = areasInPlay(state);
  const toRun = inPlay.filter((ref) => ["ready", "stale", "failed"].includes(areaStatus(state, ref))).length;
  const running = inPlay.some((ref) => areaStatus(state, ref) === "running");
  // Only results whose run matches what is drawn now: a stale one is not geometry to review.
  const shown = inPlay.flatMap((ref) =>
    areaStatus(state, ref) === "done" && results[ref.key] !== undefined ? [results[ref.key] as RecognitionResult] : [],
  );
  const reviewer = state.createdPackages[0]?.reviewedBy ?? "";

  return (
    <aside className="sidebar">
      <header className="brand">
        <a className="brand__back" href="/">
          &larr; DesignTools
        </a>
        <div className="brand__identity">
          {/* Decorative, so alt is empty on purpose: the mark says the same thing as the
              words beside it, and a screen reader announcing "Plan Forge" twice is worse
              than it announcing it once. The 512px file rather than the favicon, so it stays
              crisp on a high-density display. */}
          <img className="brand__mark" src="/icon.png" alt="" width={40} height={40} />
          <div>
            <h1 className="brand__title">Plan Forge</h1>
            <p className="brand__sub">Web preview</p>
          </div>
        </div>
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={done}
          aria-valuemin={0}
          aria-valuemax={STEPS.length}
          aria-label="Steps complete"
        >
          <div className="progress__fill" style={{ width: `${(done / STEPS.length) * 100}%` }} />
        </div>
        <p className="brand__progress">
          {done} of {STEPS.length} steps complete
        </p>

        {props.user !== null ? (
          <p className="brand__user">
            <span title={props.user.username ?? undefined}>
              {props.user.name ?? props.user.username ?? "Signed in"}
            </span>
            <button type="button" className="brand__signout" onClick={props.onSignOut}>
              Sign out
            </button>
          </p>
        ) : (
          // Not decoration. A build with no Entra configuration talks to an API that anyone
          // can reach, and that should be visible rather than inferred from the absence of a
          // name in the corner.
          <p className="brand__user brand__user--open">Not signed in - this build has no authentication</p>
        )}
      </header>

      <ProjectsPanel
        projects={props.projects}
        projectId={props.projectId}
        onOpen={props.onOpenProject}
        onDelete={props.onDeleteProject}
      />

      <div className="steps">
        <Step index={1} id="output" title="Output type" status={status("output")} defaultOpen>
          <Choice
            type="radio"
            name="output-type"
            checked={state.outputType === "DwgFloorPlan"}
            onChange={() => props.onPatch({ outputType: "DwgFloorPlan" as OutputType })}
            label="X1 .DWG Floor Plan"
            hint="Walls, doors and rooms."
          />
          <Choice
            type="radio"
            name="output-type"
            checked={state.outputType === "EpicMap"}
            onChange={() => props.onPatch({ outputType: "EpicMap" as OutputType })}
            label="EPIC Map (.PNG)"
            hint="Walls only, as an image."
          />
        </Step>

        <Step index={2} id="source" title="Floor-plan file" status={status("source")} defaultOpen>
          <FilePicker onFile={props.onFile} />
          <p className="hint">Or drop a file anywhere on this window.</p>
        </Step>

        <Step index={3} id="details" title="School and district information" status={status("details")}>
          <Details value={state.details} onChange={props.onDetails} />
        </Step>

        <Step index={4} id="preview" title="Preview" status={status("preview")}>
          <div className="row">
            <button type="button" className="btn" onClick={() => props.onPatch({ viewRotation: ((state.viewRotation + 90) % 360) as 0 | 90 | 180 | 270 })}>
              Rotate 90°
            </button>
            <button type="button" className="btn" onClick={props.onFit}>
              Fit page
            </button>
          </div>
          <div className="row">
            <button type="button" className="btn" onClick={() => props.onZoom(Math.max(0.05, zoom / 1.25))} aria-label="Zoom out">
              −
            </button>
            <output className="zoom">{Math.round(zoom * 100)}%</output>
            <button type="button" className="btn" onClick={() => props.onZoom(Math.min(8, zoom * 1.25))} aria-label="Zoom in">
              +
            </button>
          </div>
        </Step>

        <Step index={5} id="calibration" title="Scale calibration" status={status("calibration")}>
          <p className="hint">
            {mode === "calibrate"
              ? thisPage.calibration.from === null
                ? "Click the first point on the plan."
                : thisPage.calibration.to === null
                  ? "Now click the second point."
                  : "Check the line, then confirm. Click again to start over."
              : "Click two points a known distance apart, such as a 3'-0\" door."}
          </p>
          <button
            type="button"
            className={mode === "calibrate" && thisPage.calibration.to !== null ? "btn btn--primary" : mode === "calibrate" ? "btn btn--active" : "btn"}
            onClick={() => props.onMode(mode === "calibrate" ? "pan" : "calibrate")}
            disabled={page === null}
            aria-pressed={mode === "calibrate"}
          >
            {mode !== "calibrate"
              ? "Pick two calibration points"
              : thisPage.calibration.to !== null
                ? "Confirm points"
                : "Cancel"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() =>
              props.onPage(state.pageNumber, (setup) => ({
                ...setup,
                calibration: { ...setup.calibration, from: null, to: null },
              }))
            }
            disabled={thisPage.calibration.from === null}
          >
            Clear points
          </button>
          <Field
            label="Actual distance"
            value={thisPage.calibration.distanceText}
            onChange={(distanceText) =>
              props.onPage(state.pageNumber, (setup) => ({
                ...setup,
                calibration: { ...setup.calibration, distanceText },
              }))
            }
            hint={`For example 3'-0" or 36".`}
            invalid={
              thisPage.calibration.from !== null &&
              thisPage.calibration.to !== null &&
              !status("calibration").complete
            }
          />
          {source !== null && source.pageCount > 1 && (
            <p className="hint" aria-live="polite">
              Calibration: {calibrationSummary(state)}
            </p>
          )}
        </Step>

        <Step index={6} id="area" title="Conversion area" status={status("area")}>
          {source !== null && source.pageCount > 1 && (
            <PagePicker
              pageNumber={state.pageNumber}
              pageCount={source.pageCount}
              onGoToPage={props.onGoToPage}
            />
          )}

          <FloorCount
            count={thisPage.floorCount}
            onChange={(count) => props.onFloorCount(state.pageNumber, count)}
          />

          <ul className="areas">
            {floorIndexes(state, state.pageNumber).map((areaIndex) => {
              const draft = areaDraft(state, state.pageNumber, areaIndex);
              const drawing = mode === "draw" && state.areaIndex === areaIndex;
              const label =
                draft.polygon.length === 0
                  ? "not drawn"
                  : draft.polygonClosed
                    ? `${draft.polygon.length} vertices`
                    : `${draft.polygon.length} point${draft.polygon.length === 1 ? "" : "s"}, not finished`;
              return (
                <li
                  key={areaIndex}
                  className={state.areaIndex === areaIndex ? "areas__row areas__row--active" : "areas__row"}
                >
                  <button
                    type="button"
                    className="areas__name"
                    onClick={() => props.onSelectArea(areaIndex)}
                    aria-pressed={state.areaIndex === areaIndex}
                  >
                    Area {areaIndex + 1}: {label}
                  </button>
                  <div className="row">
                    <button
                      type="button"
                      className={drawing ? "btn btn--active" : "btn"}
                      onClick={() => (drawing ? props.onMode("pan") : props.onDrawArea(areaIndex))}
                      disabled={page === null}
                      aria-pressed={drawing}
                    >
                      {drawing ? "Cancel" : draft.polygonClosed ? "Redraw" : draft.polygon.length > 0 ? "Restart" : "Draw"}
                    </button>
                    {draft.polygon.length > 0 && (
                      <button type="button" className="btn btn--quiet" onClick={() => props.onClearArea(areaIndex)}>
                        Clear
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={props.onUndoPoint}
              disabled={activeArea.polygon.length === 0}
            >
              Undo last point
            </button>
            <button
              type="button"
              className="btn"
              onClick={props.onFinishArea}
              disabled={activeArea.polygon.length < MIN_POLYGON_POINTS || activeArea.polygonClosed}
            >
              Finish polygon
            </button>
          </div>
          <p className="hint">
            Draw around each floor to convert. Double-click or press Enter to finish; right-click
            undoes a point.
          </p>

          <Choice
            type="checkbox"
            checked={thisPage.skipKeyplan}
            onChange={(skipKeyplan) => props.onPage(state.pageNumber, (setup) => ({ ...setup, skipKeyplan }))}
            label="Part of a split floor plan"
            hint="No keyplan is made for this floor."
          />
          {source !== null && source.pageCount > 1 && (
            <p className="hint" aria-live="polite">
              Page {state.pageNumber}: {pageLine(state)}
            </p>
          )}
        </Step>

        <Step index={7} id="recognition" title="Recognition quality check" status={status("recognition")} defaultOpen>
          <button type="button" className="btn btn--primary" onClick={props.onRecognise} disabled={blocked !== null}>
            {toRun > 1 ? `Run recognition (${toRun} areas)` : "Run recognition"}
          </button>
          {blocked !== null && <p className="blocked">{blocked}</p>}
          {uploadFraction !== null && (
            <div
              className="progress progress--inline"
              role="progressbar"
              aria-valuenow={Math.round(uploadFraction * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Upload"
            >
              <div className="progress__fill" style={{ width: `${uploadFraction * 100}%` }} />
            </div>
          )}
          {props.progress !== null && (
            <p className="hint" aria-live="polite">
              {props.progress}
            </p>
          )}
          {running && (
            <p className="hint" aria-live="polite">
              Recognising. The first run can take a few minutes to start.
            </p>
          )}

          {inPlay.length > 0 && (
            <ul className="areas areas--status">
              {inPlay.map((ref) => (
                <li key={ref.key} className="areas__status">
                  <span>
                    Page {ref.pageNumber}, area {ref.areaIndex + 1}
                  </span>
                  <span className={`badge badge--${areaStatus(state, ref)}`}>{STATUS_WORDS[areaStatus(state, ref)]}</span>
                </li>
              ))}
            </ul>
          )}

          {shown.length > 0 && (
            <>
              <button type="button" className="btn" onClick={props.onToggleResult} aria-pressed={showResult}>
                {showResult ? "Hide recognised geometry" : "Show recognised geometry"}
              </button>
              <SheetPlanSummary
                state={state}
                done={inPlay.flatMap((ref) =>
                  areaStatus(state, ref) === "done" && results[ref.key] !== undefined
                    ? [{ key: ref.key, pageNumber: ref.pageNumber, result: results[ref.key] as RecognitionResult }]
                    : [],
                )}
              />
            </>
          )}
        </Step>

        <Step index={8} id="output-package" title="Create output" status={status("output-package")} defaultOpen>
          <details className="summary">
            <summary>Summary</summary>
            <ul>
              {reviewLines(state, source).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </details>

          <button
            type="button"
            className="btn btn--primary"
            onClick={props.onCreatePackage}
            disabled={props.packageBlocked !== null}
          >
            {state.outputType === "DwgFloorPlan" ? "Create X1 AutoCAD Package" : "Create EPIC map"}
          </button>
          {props.packageBlocked !== null && <p className="blocked">{props.packageBlocked}</p>}
          {props.packageBlocked === null && state.packagePhase !== "creating" && (
            <p className="hint">
              Creating the output confirms you have checked the recognised geometry on the plan.
              {state.outputType === "DwgFloorPlan" && canChooseSaveLocation() && " You choose where the file is saved."}
            </p>
          )}

          {state.packagePhase === "creating" && (
            <p className="hint" aria-live="polite">
              Creating the output...
            </p>
          )}

          {state.createdPackages.length > 0 && (
            <div className="packaged" role="status">
              {/* Both outputs are same-origin blobs this browser built, so `download` names
                  the file and the link does not expire. */}
              {state.createdPackages.map((made) =>
                made.saved === true ? (
                  <p key={made.url}>Saved {made.fileName}</p>
                ) : (
                  <a key={made.url} className="btn" href={made.url} download={made.fileName}>
                    Save {made.fileName}
                  </a>
                ),
              )}

              {state.outputType === "EpicMap" ? (
                <p className="hint">One image per area. Save them before starting another conversion.</p>
              ) : (
                <p className="hint">
                  In AutoCAD, type <strong>AEFI</strong> and choose this file.
                  {reviewer !== "" && ` Checked by ${reviewer}.`}
                </p>
              )}
            </div>
          )}
        </Step>

        <NewProject onConfirm={props.onNewProject} />
      </div>

      {props.warning !== null && (
        <div className="notice" role="status">
          <p className="notice__title">Review this page carefully</p>
          <p>{props.warning}</p>
        </div>
      )}

      {problem !== null && (
        <div className="problem" role="alert">
          <p className="problem__title">That did not work</p>
          <ul>
            {problem.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

function Step({
  index,
  title,
  status,
  defaultOpen = false,
  children,
}: {
  index: number;
  id: StepId;
  title: string;
  status: { complete: boolean; summary: string };
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details className="step" open={defaultOpen}>
      <summary className="step__summary">
        <span className={status.complete ? "step__num step__num--done" : "step__num"} aria-hidden="true">
          {status.complete ? "✓" : index}
        </span>
        <span className="step__titles">
          <span className="step__title">{title}</span>
          <span className="step__status">{status.summary}</span>
        </span>
      </summary>
      <div className="step__body">{children}</div>
    </details>
  );
}

/**
 * A labelled form control with a hint under it.
 *
 * One component for both radios and checkboxes, because the part worth not duplicating is the
 * markup-and-CSS contract - `.choice`, the generated id, the label wrapping two spans - rather
 * than the input's type attribute. `name` is what makes a set of radios one group and is
 * meaningless on a checkbox, so it is optional.
 */
function Choice({
  type,
  name,
  checked,
  onChange,
  label,
  hint,
}: {
  type: "radio" | "checkbox";
  name?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
  hint: string;
}) {
  const id = useId();
  return (
    <div className="choice">
      <input
        type={type}
        id={id}
        name={name}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id}>
        <span className="choice__label">{label}</span>
        <span className="choice__hint">{hint}</span>
      </label>
    </div>
  );
}

function Field({ label, value, onChange, hint, invalid, onBlur, warning }: { label: string; value: string; onChange: (value: string) => void; hint?: string; invalid?: boolean; onBlur?: () => void; warning?: string | null }) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        aria-describedby={hint === undefined ? undefined : hintId}
        aria-invalid={invalid === true || (warning !== undefined && warning !== null)}
      />
      {warning !== undefined && warning !== null && <p className="field__hint field__hint--bad">{warning}</p>}
      {hint !== undefined && (
        <p className={invalid === true ? "field__hint field__hint--bad" : "field__hint"} id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}

function Details({ value, onChange }: { value: SchoolDetails; onChange: (value: SchoolDetails) => void }) {
  const set = (key: keyof SchoolDetails) => (next: string) => onChange({ ...value, [key]: next });
  const [blurred, setBlurred] = useState<ReadonlySet<string>>(new Set());
  const touch = (field: string) => setBlurred((current) => new Set(current).add(field));

  // Spelled out when the operator leaves the field, as the desktop application does, so what
  // they see is what goes into the drawing's title block.
  const expandSchool = () =>
    onChange({
      ...value,
      schoolName: expandDistrictAbbreviations(expandSchoolTypeAbbreviations(value.schoolName), value.state),
    });
  const expandDistrict = () =>
    onChange({ ...value, districtName: expandDistrictAbbreviations(value.districtName, value.state) });

  const gaps = (address: string, field: string) =>
    blurred.has(field) && addressGaps(address).length > 0
      ? `This address may be missing: ${addressGaps(address).join(", ")}.`
      : null;

  const countryId = useId();
  return (
    <>
      <div className="field">
        <label htmlFor={countryId}>Country</label>
        <select
          id={countryId}
          value={value.country}
          onChange={(event) => onChange({ ...value, country: event.target.value as SchoolDetails["country"] })}
        >
          <option>United States</option>
          <option>Canada</option>
        </select>
      </div>
      <Field label="School name" value={value.schoolName} onChange={set("schoolName")} onBlur={expandSchool} />
      <Field
        label="School address"
        value={value.schoolAddress}
        onChange={set("schoolAddress")}
        onBlur={() => touch("school")}
        warning={gaps(value.schoolAddress, "school")}
      />
      <Field label="District name" value={value.districtName} onChange={set("districtName")} onBlur={expandDistrict} />
      <Field
        label="District address"
        value={value.districtAddress}
        onChange={set("districtAddress")}
        onBlur={() => touch("district")}
        warning={gaps(value.districtAddress, "district")}
      />
      <Field
        label="State"
        value={value.state}
        onChange={set("state")}
        hint="For example FL or Florida."
      />
      <Field
        label="Portal ID"
        value={value.portalId}
        onChange={set("portalId")}
        warning={portalIdIsInvalid(value.portalId) ? "A Portal ID is usually six digits." : null}
      />
    </>
  );
}

function PagePicker({
  pageNumber,
  pageCount,
  onGoToPage,
}: {
  pageNumber: number;
  pageCount: number;
  onGoToPage: (pageNumber: number) => void;
}) {
  const [typed, setTyped] = useState("");
  const go = () => {
    const n = Number.parseInt(typed, 10);
    if (Number.isFinite(n)) onGoToPage(n);
    setTyped("");
  };
  return (
    <div className="pager">
      <span className="pager__label">PDF page</span>
      <div className="row">
        <button type="button" className="btn" onClick={() => onGoToPage(pageNumber - 1)} disabled={pageNumber <= 1} aria-label="Previous page">
          ◀
        </button>
        <output className="pager__count">
          {pageNumber} / {pageCount}
        </output>
        <button type="button" className="btn" onClick={() => onGoToPage(pageNumber + 1)} disabled={pageNumber >= pageCount} aria-label="Next page">
          ▶
        </button>
      </div>
      <div className="row">
        <input
          className="pager__input"
          inputMode="numeric"
          aria-label="Go to page"
          placeholder="Page number"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && go()}
        />
        <button type="button" className="btn" onClick={go} disabled={typed === ""}>
          Go
        </button>
      </div>
    </div>
  );
}

function FloorCount({ count, onChange }: { count: number; onChange: (count: number) => void }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>Floors on this page</label>
      <div className="row">
        <button type="button" className="btn" onClick={() => onChange(count - 1)} disabled={count <= 1}>
          - Fewer
        </button>
        <input
          id={id}
          className="floors__count"
          inputMode="numeric"
          value={count}
          onChange={(event) => {
            const n = Number.parseInt(event.target.value, 10);
            if (Number.isFinite(n)) onChange(n);
          }}
        />
        <button type="button" className="btn" onClick={() => onChange(count + 1)} disabled={count >= MAX_FLOORS_PER_PAGE}>
          + More
        </button>
      </div>
    </div>
  );
}

/** "2 of 3 floor areas drawn", and whose scale the page uses. */
function pageLine(state: ConversionState): string {
  const floors = floorIndexes(state, state.pageNumber);
  const drawn = floors.filter((i) => areaDraft(state, state.pageNumber, i).polygonClosed).length;
  const scale = effectiveScale(state, state.pageNumber);
  const scaleText =
    scale === null ? "scale not set" : scale.sourcePage === state.pageNumber ? "scale set" : `uses page ${scale.sourcePage}'s scale`;
  return `${scaleText}; ${drawn} of ${floors.length} floor area${floors.length === 1 ? "" : "s"} drawn.`;
}

const STATUS_WORDS: Readonly<Record<AreaStatus, string>> = {
  undrawn: "Not drawn",
  drawing: "Drawing",
  ready: "Ready",
  running: "Recognising",
  failed: "Failed - will retry",
  stale: "Out of date",
  done: "Recognised",
};

/**
 * The desktop application's New Project, and its safeguards: a button set well apart from the
 * others, and a confirmation that defaults to cancelling. Everything on the page is cleared;
 * the previous project stays in the list above.
 */
function NewProject({ onConfirm }: { onConfirm: () => void }) {
  const [asking, setAsking] = useState(false);
  return (
    <div className="new-project">
      {asking ? (
        <div className="new-project__confirm" role="group" aria-label="Start a new project">
          <p>
            Start a new project? The map, every conversion area, the calibration and all
            recognition results on this page are cleared. Anything you have not exported is not
            in the file you downloaded, though it stays in your saved projects.
          </p>
          <div className="row">
            <button type="button" className="btn" onClick={() => setAsking(false)} autoFocus>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => {
                setAsking(false);
                onConfirm();
              }}
            >
              New project
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn--quiet" onClick={() => setAsking(true)}>
          New Project
        </button>
      )}
    </div>
  );
}

function ProjectsPanel({
  projects,
  projectId,
  onOpen,
  onDelete,
}: {
  projects: readonly ProjectSummary[];
  projectId: string;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);
  if (projects.length === 0) return null;
  return (
    <details className="projects">
      <summary>Saved projects ({projects.length})</summary>
      <p className="hint">Kept in this browser only, with the plan file.</p>
      <ul>
        {projects.map((project) => (
          <li key={project.id} className="projects__row">
            <button
              type="button"
              className="areas__name"
              onClick={() => onOpen(project.id)}
              aria-current={project.id === projectId}
              title={project.fileName}
            >
              {project.name}
              <span className="projects__when">{new Date(project.updatedAt).toLocaleString()}</span>
            </button>
            {confirming === project.id ? (
              <span className="projects__confirm">
                <button type="button" className="btn btn--danger" onClick={() => { setConfirming(null); onDelete(project.id); }} aria-label={`Confirm delete ${project.name}`}>
                  Delete
                </button>
                <button type="button" className="btn" onClick={() => setConfirming(null)} autoFocus>
                  Keep
                </button>
              </span>
            ) : (
              <button type="button" className="btn btn--quiet" onClick={() => setConfirming(project.id)} aria-label={`Delete ${project.name}`}>
                Delete
              </button>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function FilePicker({ onFile }: { onFile: (file: File) => void }) {
  const id = useId();
  return (
    <>
      <input
        id={id}
        className="file-input"
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) onFile(file);
        }}
      />
      <label className="btn btn--file" htmlFor={id}>
        Open PDF / JPG / PNG
      </label>
    </>
  );
}

/** What the 03 drawing will be cut into, so a bad cut is seen here rather than in AutoCAD. */
function SheetPlanSummary({
  state,
  done,
}: {
  state: ConversionState;
  done: readonly { key: string; pageNumber: number; result: RecognitionResult }[];
}) {
  if (state.outputType !== "DwgFloorPlan") return null;
  const previews = done.flatMap(({ key, pageNumber, result }) => {
    const scale = effectiveScale(state, pageNumber)?.scale;
    if (scale === undefined) return [];
    const preview = sheetPreviewFor(result, scale);
    return preview.plan === null && !preview.failed ? [] : [{ key, pageNumber, ...preview }];
  });
  if (previews.length === 0) return null;
  return (
    <>
      {previews.map(({ key, pageNumber, plan, failed }) =>
        plan === null || failed ? (
          <p key={key} className="hint">
            Could not preview the 03 sheets for page {pageNumber}. The package will tile it on the old grid.
          </p>
        ) : (
          <p key={key} className="hint">
            03 drawing: {sheetSummary(plan)}.
            {plan.warnings.map((warning) => (
              <span key={warning}> {warning}</span>
            ))}
          </p>
        ),
      )}
    </>
  );
}
